import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { AngularFireStorage } from '@angular/fire/storage';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { UserSpot } from '../interfaces/user-spot.model';
import { StoreSpot } from '../interfaces/store-spot.model';
import { UserProfile } from '../interfaces/user.model';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})
export class FirebaseService {
    constructor(
        private fireDb: AngularFirestore,
        private fireStorage: AngularFireStorage
    ) { }

    // -------------------------------------
    /** Saves User's Spot data on Firebase  */
    saveUserSpotData(spot: UserSpot) {
        return this.fireDb.collection('userSpots').add(spot).then(ref => console.log(ref));
    }

    // -------------------------------------
    /** Saves User's Store Spot data on Firebase  */
    saveStoreSpotData(storeSpot: StoreSpot) {
        return this.fireDb.collection('userStoreSpots').add(storeSpot).then(ref => console.log(ref));
    }

    // -------------------------------------
    /** returns the spotted store by id  */
    getSpottedStore(storeId: string): Observable<StoreSpot[]> {
        return this.fireDb.collection('userStoreSpots', ref => ref
            .where('store_id', '==', storeId)
        ).get().pipe(
            map(snap => {
                if (snap.docs.length > 0) {
                    return snap.docs.map(doc => doc.data() as StoreSpot);
                }
                return null;
            })
        );
    }

    // -------------------------------------
    /**
     * retrns number of spots for user during time range
     * @param userId user's email address
     * @param start start time
     * @param end end time
     */
    getUserSpotCount(userId: string, start: string, end: string) {
        const col = this.fireDb.collection('userSpots', ref => ref
            .where('spot_source_user_id', '==', userId)
            .where('spot_time', '>', start)
            .where('spot_time', '<', end)
        );

        return col.get().pipe(
            map(spots => spots.size)
        );
    }

     // -------------------------------------
    /**
     * returns spots for product during time range
     * @param productId id for product
     * @param start start time
     * @param end end time
     */
    getProductSpots(productId: string, storeAddress: string,  start: string) {
        return this.fireDb.collection('userSpots', ref => ref
            .where('product_sku', '==', productId)
            .where('store_address', '==', storeAddress)
            .where('spot_time', '>', start)

        ).get().pipe(
            map(snap => {
                if (snap.docs.length > 0) {
                    return snap.docs.map(doc => doc.data() as UserSpot);
                }
                return null;
            })
        );
    }

    // -------------------------------------
    /** Gets User's info from Firebase  */
    getUserProfile(email: string): Observable<UserProfile> {
        return this.fireDb.collection('users').doc(email).get().pipe(
            map(doc => {
                const profile = doc.data() as UserProfile;
                profile.badges = profile.badges || {};
                return profile;
            })
        );
    }

    // -------------------------------------
    /** Gets Sponsored Product List from Firebase  */
    getSponsors(): Observable<string[]> {
        return this.fireDb.collection('system').doc('sponsors').get().pipe(
            map(doc => {
                const sponsor = doc.data() as string[];
                return sponsor;
            })
        );
    }

    // -------------------------------------
    /** Saves User's info on Firebase  */
    saveUserProfile(user: UserProfile) {
        user.lastUpdate = (new Date()).toISOString();
        user.appVersion = environment.app.version;
        return this.fireDb.collection('users').doc(user.email).set(user);
    }

    // -------------------------------------
    /** Checks if the email exists in system/emails  */
    // validateEmailAddress(email: string): Observable<boolean> {
    //     email = email.toLowerCase();
    //     const col = this.fireDb.collection('system', ref => ref.where('emails', 'array-contains', email));
    //     return col.snapshotChanges().pipe(
    //         map(snap => snap.length > 0)
    //     );
    // }

    // -------------------------------------
    /**
     * returns the full image URL
     * @param filename image filename as it is stored on fire storage "images" folder
     */
    async getImageUrl(filename: string) {
        const ref = this.fireStorage.storage.ref(`images/${filename}`);
        return await ref.getDownloadURL();
    }
}
